import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Save, Key } from 'lucide-react'
import { useAuthStore } from '@/hooks/useAuth'
import { supabase } from '@/services/supabase'

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal(''))
})

const passwordSchema = z.object({
  currentPassword: z.string().min(6, 'Password must be at least 6 characters'),
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type ProfileForm = z.infer<typeof profileSchema>
type PasswordForm = z.infer<typeof passwordSchema>

export default function Profile() {
  const { user } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      bio: '',
      website: ''
    }
  })

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema)
  })

  const onUpdateProfile = async (data: ProfileForm) => {
    setIsLoading(true)
    setError('')
    setMessage('')

    try {
      // TODO: Update profile via API
      console.log('Updating profile:', data)
      setMessage('Profile updated successfully!')
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating profile')
    } finally {
      setIsLoading(false)
    }
  }

  const onChangePassword = async (data: PasswordForm) => {
    setPasswordLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      })

      if (error) throw error

      setMessage('Password updated successfully!')
      passwordForm.reset()
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating password')
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex items-center space-x-3">
        <User size={32} className="text-primary-600" />
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
      </div>

      {message && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>
          
          <form onSubmit={profileForm.handleSubmit(onUpdateProfile)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="label">
                  First Name
                </label>
                <input
                  {...profileForm.register('firstName')}
                  type="text"
                  id="firstName"
                  className="input"
                  placeholder="First name"
                />
                {profileForm.formState.errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {profileForm.formState.errors.firstName.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="label">
                  Last Name
                </label>
                <input
                  {...profileForm.register('lastName')}
                  type="text"
                  id="lastName"
                  className="input"
                  placeholder="Last name"
                />
                {profileForm.formState.errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {profileForm.formState.errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={user?.email || ''}
                disabled
                className="input bg-gray-50"
              />
              <p className="mt-1 text-sm text-gray-500">
                Email cannot be changed. Contact support if you need to update your email.
              </p>
            </div>

            <div>
              <label htmlFor="bio" className="label">
                Bio
              </label>
              <textarea
                {...profileForm.register('bio')}
                id="bio"
                rows={4}
                className="input"
                placeholder="Tell us about yourself and your writing..."
              />
              {profileForm.formState.errors.bio && (
                <p className="mt-1 text-sm text-red-600">
                  {profileForm.formState.errors.bio.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {profileForm.watch('bio')?.length || 0}/500 characters
              </p>
            </div>

            <div>
              <label htmlFor="website" className="label">
                Website
              </label>
              <input
                {...profileForm.register('website')}
                type="url"
                id="website"
                className="input"
                placeholder="https://your-website.com"
              />
              {profileForm.formState.errors.website && (
                <p className="mt-1 text-sm text-red-600">
                  {profileForm.formState.errors.website.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary flex items-center space-x-2 w-full"
            >
              <Save size={18} />
              <span>{isLoading ? 'Updating...' : 'Update Profile'}</span>
            </button>
          </form>
        </div>

        {/* Security Settings */}
        <div className="space-y-6">
          {/* Change Password */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
            
            <form onSubmit={passwordForm.handleSubmit(onChangePassword)} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="label">
                  Current Password
                </label>
                <input
                  {...passwordForm.register('currentPassword')}
                  type="password"
                  id="currentPassword"
                  className="input"
                  placeholder="Enter current password"
                />
                {passwordForm.formState.errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.currentPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="newPassword" className="label">
                  New Password
                </label>
                <input
                  {...passwordForm.register('newPassword')}
                  type="password"
                  id="newPassword"
                  className="input"
                  placeholder="Enter new password"
                />
                {passwordForm.formState.errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  Confirm New Password
                </label>
                <input
                  {...passwordForm.register('confirmPassword')}
                  type="password"
                  id="confirmPassword"
                  className="input"
                  placeholder="Confirm new password"
                />
                {passwordForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="btn btn-secondary flex items-center space-x-2 w-full"
              >
                <Key size={18} />
                <span>{passwordLoading ? 'Updating...' : 'Change Password'}</span>
              </button>
            </form>
          </div>

          {/* Account Stats */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Statistics</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Member since:</span>
                <span className="font-medium">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total works:</span>
                <span className="font-medium">12</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total submissions:</span>
                <span className="font-medium">28</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Acceptances:</span>
                <span className="font-medium text-green-600">5</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}